import { normalizeWidgetTheme, APIURL, cancelSelectEvent, changeSymbolForWidget, getCSSStyles, getNoDataHTML, getSymbolDropdown, getTraderSMCardHTML, getWidgetDataURI, isEmptyWidgetData, apiGet, toggleWidgetTheme, updateSelectedSymbolInDropdown, isHeaderPinned, getHeaderPinStateClass, getHeaderPinButtonHTML, setupHeaderPin } from './../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from './../../types';

interface PerformanceData {
  period: string;
  prevClosePrice: number;
  performanceLastDate: string;
  periodOpen: number;
  periodOpenPercentage: number;
  periodLow: number;
  periodLowPercentage: number;
  barPositionPercentage: number;
  periodHigh: number;
  periodHighPercentage: number;
  periodLowDate: string;
  periodHighDate: string;
  performance: number;
  performancePercentage: number;
  startClose: number;
  endClose: number;
  performanceDate: string;
  chart: number[];
}

interface PastPricePerformanceConfig extends WidgetConfig {
  interval?: string; 
}

export class TraderversePricePerformance {
  private container: HTMLElement;
  private config: PastPricePerformanceConfig;
  private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
  private shadowRoot: ShadowRoot;
  constructor(config: WidgetConfig) {
    this.config = { ...this.getDefaultConfig(), ...config } as PastPricePerformanceConfig;
    this.config.theme = normalizeWidgetTheme(this.config.theme);
    this.container = this.resolveContainer(config.container);

    if ((this.container as any).shadowRoot) {
      this.shadowRoot = (this.container as any).shadowRoot;
    } else {
      this.shadowRoot = this.container.attachShadow({ mode: "closed" });
    }

    this.init();
  }

  private getDefaultConfig(): Partial<PastPricePerformanceConfig> {
    return {
      headerPinned: true,
      theme: 'default-dark',
      width: '100%',
      height: '400px',
      autosize: true,
      interval: '5d'
    };
  }

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    if (typeof container === 'string') {
      const element = document.getElementById(container) || document.querySelector(container);
      if (!element) {
        throw new Error(`Container not found: ${container}`);
      }
      return element as HTMLElement;
    }
    return container;
  }

  private async loadECharts(): Promise<void> {
    if (!(window as any).echarts) {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/echarts@5.4.3/dist/echarts.min.js';
      document.head.appendChild(script);

      return new Promise((resolve) => {
        script.onload = () => resolve();
      });
    }
  }


  private changeSymbol(stockId: string, stock: any = null): void {
    changeSymbolForWidget(this.shadowRoot, this.config, stock, stockId, (stockId: any) => this.loadPerformanceData(stockId), (type: any, data: any) => this.emit(type, data));
  }

  private setupEventListeners(): void {
    setupHeaderPin(this.shadowRoot, this.config);
    const symbolDropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;

    symbolDropdown?.addEventListener('change', (e) => {
      const symbol = (e.target as HTMLSelectElement).value;
      this.changeSymbol(symbol);
    });

    const periodDropdown = this.shadowRoot.querySelector('.period-dropdown') as HTMLSelectElement;
    periodDropdown?.addEventListener('change', (e) => {
      const interval = (e.target as HTMLSelectElement).value; // '1d' | '5d'
      this.config.interval = interval;
      const currentSymbol = this.config.symbol || 'MSFT';
      this.loadPerformanceData(currentSymbol);
    });
    if (periodDropdown && this.config.interval) {
      periodDropdown.value = this.config.interval;
    }

  }


  public setSymbol(symbol: string): void {
    this.changeSymbol(symbol);
    const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
    if (dropdown) dropdown.value = symbol;
  }

  private async init(): Promise<void> {
    this.createWidget();
    await this.loadECharts();
    this.attachStyles();
    this.setupEventListeners();
    this.emit('ready', {});
    cancelSelectEvent(this.shadowRoot);
    this.config.onReady?.();
  }


  private fetchSymbols(): Array<{ value: string; label: string }> {
    return [
      {
        "label": "AAPL",
        "value": "AAPL"
      },
      {
        "label": "MSFT",
        "value": "MSFT"
      },
      {
        "label": "GOOGL",
        "value": "GOOGL"
      },
      {
        "label": "AMZN",
        "value": "AMZN"
      },
      {
        "label": "NVDA",
        "value": "NVDA"
      },
      {
        "label": "META",
        "value": "META"
      },
      {
        "label": "TSLA",
        "value": "TSLA"
      },
      {
        "label": "AVGO",
        "value": "AVGO"
      },
      {
        "label": "PEP",
        "value": "PEP"
      },
      {
        "label": "COST",
        "value": "COST"
      },
      {
        "label": "CSCO",
        "value": "CSCO"
      },
      {
        "label": "ADBE",
        "value": "ADBE"
      },
      {
        "label": "TMUS",
        "value": "TMUS"
      },
      {
        "label": "AMD",
        "value": "AMD"
      },
      {
        "label": "NFLX",
        "value": "NFLX"
      },
      {
        "label": "INTC",
        "value": "INTC"
      },
      {
        "label": "AMGN",
        "value": "AMGN"
      },
      {
        "label": "QCOM",
        "value": "QCOM"
      },
      {
        "label": "TXN",
        "value": "TXN"
      },
      {
        "label": "HON",
        "value": "HON"
      }
    ];
  }
  

  private createWidget(): void {
    let widgetInstanceCounter = 0;
    const widgetIndex = widgetInstanceCounter++;
    const symbolOptions = this.fetchSymbols().map(s => `<option value="${s.value}">${s.label}</option>`).join('');
let themeClass = 'defaultDarkMode';
let dataTheme = 'default-dark';

if (this.config.theme === 'white') {
  themeClass = 'whiteMode';
  dataTheme = 'white';
} else if (this.config.theme === 'black') {
  themeClass = 'blackMode';
  dataTheme = 'black';
} else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}
    const widgetHtml = `
              <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width  };height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${this.config.theme}" data-testing-id="asset-price-widget-${widgetIndex}">
          <div class="headTop">
            <h4 class="title stock">${this.getTitleIconSvg()} Price Performance</h4>
            <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
              <i class="pi pi-ellipsis-v"></i>
            </div>
          </div>
          <div class="inBody">

            <div class="optionsRow">
                ${this.config.hideSymbol ? '<div class="item dropStyle01"></div>' : `<div class="item dropStyle01">
                <div class="trader-sm-card" id="selected-symbol">
                ${getTraderSMCardHTML(this.config.stock ?? { symbol: this.config.symbol!, type: "stock", name: "" })}
                </div>
                <span id="symbol-dropdown-placeholder"></span>
                </div>`}
              <span></span>
              <div class="item">
                <select class="period-dropdown">
                  <option value="1d">1D</option>
                  <option value="5d" selected>5D</option>
                </select>
              </div>
            </div>

            <div class="chartDv" id="pastPricePerformanceContainer">
            </div>

          </div>
        </div>
    `;

    this.shadowRoot.innerHTML = widgetHtml;
    if (this.config.isSymbolSelectable && !this.config.hideSymbol) {
      const dropdown = getSymbolDropdown((data: any) => this.onSelectChange(data));
      const placeholder = this.shadowRoot.getElementById('symbol-dropdown-placeholder');
      if (placeholder && dropdown) {
        placeholder.replaceWith(dropdown);
      }
    }
    this.loadPerformanceData(this.config.symbol || 'MSFT');
  }

  onSelectChange(stock: any) {
    this.changeSymbol(stock.symbol, stock);
  }

  private async attachStyles(): Promise<void> {
    try {
      const styleEl = await getCSSStyles();
      this.shadowRoot.appendChild(styleEl);
    } catch (err) {
      console.error("Failed to load CSS file:", err);
    }
  }


  private async loadPerformanceData(symbol: string = this.config.symbol || "MSFT"): Promise<void> {
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    if (widget) widget.classList.add('loading');
    try {
      const performanceData = await this.fetchPerformanceData(symbol, this.config.interval || '5d');
      this.renderPerformanceData(performanceData);
      if (widget) widget.classList.remove('loading');
    } catch (error) {
      console.error('Failed to load performance data:', error);
      this.renderError();
      if (widget) widget.classList.remove('loading');
    }
  }

  private async fetchPerformanceData(symbol: string, interval: string): Promise<PerformanceData[]> {
    const startDate = new Date();
    const endDate = new Date();
    endDate.setDate(startDate.getDate() + 4);

    return new Promise((resolve) => {

      apiGet({
        method: "POST",
        url: `${APIURL}/v3/ticker/price/performance`,
        body: { symbol, interval },
      }).then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      }).then((responseData) => {
        const performanceData: PerformanceData[] = responseData.data.map((item: any) => ({
          period: item.period,
          prevClosePrice: item.prevClosePrice,
          performanceLastDate: item.performanceLastDate,
          periodOpen: item.periodOpen,
          periodOpenPercentage: item.periodOpenPercentage,
          periodLow: item.periodLow,
          periodLowPercentage: item.periodLowPercentage,
          barPositionPercentage: item.barPositionPercentage,
          periodHigh: item.periodHigh,
          periodHighPercentage: item.periodHighPercentage,
          periodLowDate: item.periodLowDate,
          periodHighDate: item.periodHighDate,
          performance: item.performance,
          performancePercentage: item.performancePercentage,
          startClose: item.startClose,
          endClose: item.endClose,
          performanceDate: item.performanceDate,
          chart: item.chart
        }));
        resolve(performanceData);
      });
    });
  }

  private renderPerformanceData(data: PerformanceData[]): void {
    const container = this.shadowRoot.querySelector('#pastPricePerformanceContainer');
    if (!container) return;
    if (isEmptyWidgetData(data)) {
      container.innerHTML = getNoDataHTML();
      return;
    }

    const html = data.map((item, index) => {
      const chartId = `chart-${index}`;
      return `
    <div class="pppMain" data-testing-id="price-performance-widget-${index}">
      <div class="pppItem">
        <div class="periodTxtBar">
          <div class="lowBar">
            <p>Period Low</p>
            <h5>$${item.periodLow.toFixed(2)} <span>${item.periodLowPercentage.toFixed(2)}%</span></h5>
            <p>on ${item.periodLowDate}</p>
          </div>
          <div class="highBar ${item.periodHighPercentage < 0 ? 'down' : ''}">
            <p>Period High</p>
            <h5>$${item.periodHigh.toFixed(2)} <span>${item.periodHighPercentage.toFixed(2)}%</span></h5>
            <p>on ${item.periodHighDate}</p>
          </div>
        </div>
        <div class="statbar"><span style="width:${item.barPositionPercentage.toFixed(2)}%;"></span></div>
        <p>Previous Close <span>$${item.prevClosePrice.toFixed(2)}</span></p>
      </div>
    </div>
  `;
    }).join('');


    container.innerHTML = html;

    // Render charts after DOM is updated
    setTimeout(() => {
      data.forEach((item, index) => {
        this.renderChart(`chart-${index}}`, item);
      });
    }, 100);
  }

  private renderChart(chartId: string, item: PerformanceData): void {
    const chartDom = this.shadowRoot.getElementById(chartId);
    if (!chartDom || !(window as any).echarts) return;

    const myChart = (window as any).echarts.init(chartDom);
    myChart.setOption({
      grid: { left: 0, right: 0, top: 10, bottom: 10 },
      xAxis: {
        type: 'category',
        show: false,
        data: Array(item.chart.length).fill('')
      },
      yAxis: {
        type: 'value',
        show: false
      },
      series: [{
        data: item.chart,
        type: 'line',
        smooth: true,
        symbol: 'none',
        lineStyle: {
          color: item.performancePercentage < 0 ? '#ff3b3b' : '#00da3c',
          width: 2
        },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              {
                offset: 0, color: item.performancePercentage < 0 ? 'rgba(255,59,59,0.3)' : 'rgba(0,218,60,0.3)'
              },
              {
                offset: 1, color: 'rgba(255,255,255,0)'
              }
            ]
          }
        }
      }]
    });
  }

  private getTimeAgo(dateString: string): string {
    const now = new Date();
    const publishDate = new Date(dateString);
    const diffInHours = Math.floor((now.getTime() - publishDate.getTime()) / (1000 * 60 * 60));

    if (diffInHours < 1) {
      return 'Less than an hour ago';
    } else if (diffInHours === 1) {
      return '1 hour ago';
    } else {
      return `${diffInHours} hours ago`;
    }
  }

  private renderError(): void {
    const container = this.shadowRoot.querySelector('.news-highlights-dv');
    if (!container) return;

    container.innerHTML = '<div class="error">Failed to load news data</div>';
  }

  private async saveAsImage(): Promise<void> {
    try {
      const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
      if (!widget || !(window as any).html2canvas) return;

      // Temporarily clone the widget to regular DOM for html2canvas
      const clone = widget.cloneNode(true) as HTMLElement;

      // Create a temporary container
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '-9999px';
      tempContainer.style.width = widget.offsetWidth + 'px';
      tempContainer.style.height = widget.offsetHeight + 'px';

      // Copy computed styles
      const widgetStyles = window.getComputedStyle(widget);
      for (let i = 0; i < widgetStyles.length; i++) {
        const property = widgetStyles[i];
        clone.style.setProperty(property, widgetStyles.getPropertyValue(property));
      }

      tempContainer.appendChild(clone);
      document.body.appendChild(tempContainer);

      // Capture the image
      const canvas = await (window as any).html2canvas(clone, {
        backgroundColor: null,
        scale: 2,
        useCORS: true
      });

      // Clean up
      document.body.removeChild(tempContainer);

      // Convert to blob and emit
      canvas.toBlob((blob: Blob) => {
        if (blob) {
          this.emit('ready' as any, { blob, canvas });

          // Optional: Auto-download
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `traderverse-news-${Date.now()}.png`;
          a.click();
          URL.revokeObjectURL(url);
        }
      }, 'image/png');

    } catch (error) {
      console.error('Failed to capture image:', error);
      this.emit('ready' as any, { error });
    }
  }

  private toggleTheme(): void {
    const widget = this.shadowRoot.querySelector('.widgetBox');
    if (widget) {
      const currentTheme = widget.getAttribute('data-theme');
      const newTheme = currentTheme === 'dark' ? 'light' : 'dark';
      widget.setAttribute('data-theme', newTheme);
      this.config.theme = newTheme as 'white' | 'black';
    }
  }

  private toggleFullscreen(): void {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      this.container.requestFullscreen?.();
    }
  }

  // Public API methods
  public on(eventType: WidgetEventType, handler: WidgetEventHandler): void {
    if (!this.eventHandlers.has(eventType)) {
      this.eventHandlers.set(eventType, []);
    }
    this.eventHandlers.get(eventType)!.push(handler);
  }

  public off(eventType: WidgetEventType, handler: WidgetEventHandler): void {
    const handlers = this.eventHandlers.get(eventType);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  public async getWidgetImage(): Promise<string> {
    const widget = this.shadowRoot.querySelector(".widgetBox") as HTMLElement;
    return await getWidgetDataURI(widget);
  }



  public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
  if (!this.shadowRoot) return;

  this.config.theme = toggleWidgetTheme(
    this.shadowRoot,
    theme
  ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';

  this.loadPerformanceData();
}

  
  public refresh(): void {
    this.loadPerformanceData();
  }

  public destroy(): void {
    this.eventHandlers.clear();
    this.shadowRoot.innerHTML = '';
  }

  private emit(type: WidgetEventType, data: any): void {
    const handlers = this.eventHandlers.get(type);
    if (handlers) {
      const event: WidgetEvent = { type, data };
      handlers.forEach(handler => handler(event));
    }
  }
  private getTitleIconSvg(): string {
    return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M8.59998 23.625C8.08748 23.625 7.66248 23.2 7.66248 22.6875V20.1C7.66248 19.5875 8.08748 19.1625 8.59998 19.1625C9.11248 19.1625 9.53748 19.5875 9.53748 20.1V22.6875C9.53748 23.2125 9.11248 23.625 8.59998 23.625Z" fill="currentColor"/> <path d="M15 23.625C14.4875 23.625 14.0625 23.2 14.0625 22.6875V17.5C14.0625 16.9875 14.4875 16.5625 15 16.5625C15.5125 16.5625 15.9375 16.9875 15.9375 17.5V22.6875C15.9375 23.2125 15.5125 23.625 15 23.625Z" fill="currentColor"/> <path d="M21.4 23.625C20.8875 23.625 20.4625 23.2 20.4625 22.6875V14.9125C20.4625 14.4 20.8875 13.975 21.4 13.975C21.9125 13.975 22.3375 14.4 22.3375 14.9125V22.6875C22.3375 23.2125 21.925 23.625 21.4 23.625Z" fill="currentColor"/> <path d="M8.59997 16.475C8.17497 16.475 7.79997 16.1875 7.68747 15.7625C7.56247 15.2625 7.86247 14.75 8.37497 14.625C12.975 13.475 17.025 10.9625 20.1125 7.37502L20.6875 6.70002C21.025 6.31252 21.6125 6.26252 22.0125 6.60002C22.4 6.93752 22.45 7.52502 22.1125 7.92502L21.5375 8.60002C18.2 12.5 13.8 15.2125 8.82497 16.45C8.74997 16.475 8.67497 16.475 8.59997 16.475Z" fill="currentColor"/> <path d="M21.4001 11.9C20.8876 11.9 20.4626 11.475 20.4626 10.9625V8.25H17.7375C17.225 8.25 16.8 7.825 16.8 7.3125C16.8 6.8 17.225 6.375 17.7375 6.375H21.4001C21.9126 6.375 22.3376 6.8 22.3376 7.3125V10.975C22.3376 11.4875 21.9251 11.9 21.4001 11.9Z" fill="currentColor"/> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> </svg>`;
  }

}

// Global registration for script tag usage
declare global {
  interface Window {
    TraderversePricePerformance: typeof TraderversePricePerformance;
  }
}

if (typeof window !== 'undefined') {
  window.TraderversePricePerformance = TraderversePricePerformance;
}
